import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, Component, input } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import type { IProductInfo, IUnitInfo, TicketDeliveryProduct } from "@kiraah-shared/common";
import type { IThirdPartEnterpriseInfo } from "@kiraah-shared/common";
import { ThirdPartEnterprise } from "@kiraah-shared/common";
import type { MobileTicketWizardComponent } from "../../mobile-ticket-wizard-page.component";

/** Vue équivalent web `ngx-ticket-product-selection`. Logique sur `MobileTicketWizardComponent`. */
@Component({
  selector: "mobile-ticket-product-selection-step",
  standalone: true,
  imports: [CommonModule, TranslateModule, FormsModule],
  templateUrl: "./mobile-ticket-product-selection-step.component.html",
  styleUrl: "./mobile-ticket-product-selection-step.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileTicketProductSelectionStepComponent {
  readonly wizard = input.required<MobileTicketWizardComponent>();

  protected productCatalogTrack(index: number, prod: IProductInfo): string {
    return this.wizard().productCatalogTrack(index, prod);
  }

  protected unitOptionTrack(index: number, unit: IUnitInfo): string {
    return this.wizard().unitOptionTrack(index, unit);
  }

  protected selectionLineTrack(index: number, line: TicketDeliveryProduct): string {
    return this.wizard().selectionLineTrack(index, line);
  }

  protected unitDisplayName(unitUid: string): string | null {
    const trimmed = unitUid?.trim();
    if (!trimmed) {
      return null;
    }

    const wizard = this.wizard();
    const unit = wizard.sortedUnits().find((entry) => wizard.unitUidStr(entry) === trimmed);
    return unit?.Name?.trim() || null;
  }

  protected isUnitRowSelected(unitUid: string, selectedUid: string): boolean {
    return !!unitUid && unitUid === selectedUid;
  }

  protected pickCatalogUnit(details: HTMLDetailsElement, productUid: string, unitUid: string): void {
    this.wizard().patchProductRowDraft(productUid, { unitUid });
    details.open = false;
  }

  protected pickSelectionUnit(details: HTMLDetailsElement, line: TicketDeliveryProduct, unitUid: string): void {
    this.wizard().updateSelectionLineUnit(line, unitUid);
    details.open = false;
  }

  protected openProductDetail(product: IProductInfo, event?: Event): void {
    event?.stopPropagation();
    this.wizard().openProductDetail(product);
  }

  protected productSupplierPreviewLabels(product: IProductInfo, maxVisible = 3): string[] {
    return this.productSupplierLabels(product).slice(0, maxVisible);
  }

  protected productSupplierOverflowCount(product: IProductInfo, maxVisible = 3): number {
    return Math.max(0, this.productSupplierLabels(product).length - maxVisible);
  }

  protected hasLinkedSuppliers(product: IProductInfo): boolean {
    return this.productSupplierLabels(product).length > 0;
  }

  protected supplierInitials(label: string): string {
    const supplier = ThirdPartEnterprise.createInstance(ThirdPartEnterprise, {
      CommercialName: label,
    } as IThirdPartEnterpriseInfo);
    return supplier.Initials || "?";
  }

  protected supplierAvatarBackground(label: string): string {
    const supplier = ThirdPartEnterprise.createInstance(ThirdPartEnterprise, {
      CommercialName: label,
    } as IThirdPartEnterpriseInfo);
    return supplier.AvatarBackground || "#906be6";
  }

  private productSupplierLabels(product: IProductInfo): string[] {
    const unique = new Set<string>();
    const ordered = [...(product.SupplierLabels ?? []), product.SupplierLabel ?? ""]
      .map((label) => label?.trim())
      .filter((label): label is string => !!label);

    return ordered.filter((label) => {
      const key = label.toLowerCase();
      if (unique.has(key)) {
        return false;
      }
      unique.add(key);
      return true;
    });
  }
}
